// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import { useCallback, useMemo, useState } from "react";

import { type CommentWithOptionalTags } from "./reviewCommentTags";

import { isEarlierReviewComment } from "../../utils/visibleReviewComments";

/**
 * The selection the findings list last opened a section for, as
 * `<commentId>:<section>`; null when none.
 */
export type ReviewSectionRevealMemory = {
  get: () => string | null;
  set: (revealed: string | null) => void;
};

/**
 * The "also raised in earlier reviews" section of one review: whether it is
 * open, and the comments the artwork pins and the judgment auto-advance work
 * from while it is not.
 *
 * Held beside the workspace, not in the list, because the pins follow it and
 * the list unmounts on the Feed tab. Keyed by review, so another version opens
 * closed again without an effect to reset it.
 */
export function useAskV2ReviewEarlierSection({
  reviewId,
  panelComments,
  selectedCommentId,
}: {
  reviewId: string | null;
  panelComments: readonly CommentWithOptionalTags[];
  selectedCommentId: string | null;
}) {
  const [openFor, setOpenFor] = useState<string | null>(null);
  const earlierOpen = reviewId != null && openFor === reviewId;
  const onEarlierOpenChange = useCallback(
    (open: boolean) => setOpenFor(open ? reviewId : null),
    [reviewId],
  );
  // why: the list unmounts on the Feed tab, so a marker kept there reset on
  // every remount and reopened a section she had closed while its finding was
  // still selected. Kept per review, so another version starts with none.
  const [revealedByReview] = useState(() => new Map<string, string>());
  const revealMemory = useMemo<ReviewSectionRevealMemory>(
    () => ({
      get: () =>
        reviewId == null ? null : (revealedByReview.get(reviewId) ?? null),
      set: (revealed) => {
        if (reviewId == null) return;
        if (revealed == null) revealedByReview.delete(reviewId);
        else revealedByReview.set(reviewId, revealed);
      },
    }),
    [revealedByReview, reviewId],
  );

  // why: a pin the list hides, or a judgment that advances onto a hidden card,
  // is a finding she cannot find. The open finding is always kept, so a deep
  // link or a live-findings row still lands on its pin.
  const shownComments = useMemo(
    () =>
      earlierOpen
        ? panelComments
        : panelComments.filter(
            (comment) =>
              !isEarlierReviewComment(comment) ||
              comment._id === selectedCommentId,
          ),
    [earlierOpen, panelComments, selectedCommentId],
  );

  return { earlierOpen, onEarlierOpenChange, revealMemory, shownComments };
}
